import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { styled, alpha, type Theme } from '@mui/material/styles';
import { 
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputAdornment,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MergeIcon from '@mui/icons-material/Merge';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import ArticleIcon from '@mui/icons-material/AccountTree';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShareIcon from '@mui/icons-material/Share';
import InboxIcon from '@mui/icons-material/Inbox';
import OutboxIcon from '@mui/icons-material/Outbox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Document as AppDocument } from '../../types/Document';
import {
  AcceptShareResponse,
  DocumentShareInvitation,
  filterPendingIncomingShares,
  filterPendingOutgoingShares,
} from '../../types/DocumentShare';
import {
  DocumentReadAccessGrant,
  filterActiveReadAccessGrants,
  mergeFromReadAccessGrantValue,
  readAccessGrantMergeLabel,
  readOnlyDocumentLabel,
  readOnlyDocumentTabId,
  shouldRemoveReadAccessGrantAfterOpenError,
} from '../../types/DocumentReadAccess';
import { Category } from '../../types/Category';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import { SelectiveMergeBaseline } from '../../types/DocumentCompare';
import DuplicateDocumentDialog from '../dialogs/DuplicateDocumentDialog';
import MergeDocumentDialog from '../dialogs/MergeDocumentDialog';
import { RenameDocumentDialog } from '../dialogs/RenameDocumentDialog';
import { ChooseDocumentNameDialog } from '../dialogs/ChooseDocumentNameDialog';
import SuggestedTreeDialog from '../dialogs/SuggestedTreeDialog';
import SuggestTreeContextDialog from '../dialogs/SuggestTreeContextDialog';
import ExpandTreeDialog from '../dialogs/ExpandTreeDialog';
import ExpandTreeResultsDialog from '../dialogs/ExpandTreeResultsDialog';
import ShareDocumentDialog from '../dialogs/ShareDocumentDialog';
import AcceptDocumentShareDialog from '../dialogs/AcceptDocumentShareDialog';
import mammoth from 'mammoth';
import JSZip from 'jszip';
import * as pdfjsLib from 'pdfjs-dist';
import { useDocument } from '../../contexts/DocumentContext';
import { useAuth } from '../../contexts/AuthContext';
import { FEATURE_FLAG_KEYS, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { HelpContentKey } from '../../constants/helpContent';
import {
  compareClosedDocuments,
  extractDocumentName,
  loadClosedDocumentsSort,
  saveClosedDocumentsSort,
  type ClosedDocumentsSortMode,
} from '../../utils/DocumentUtils';
import api, { skipAuthRedirectConfig } from '../../services/api';

// Configure PDF.js to use the local worker file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

function normalizeDocumentNameForFilter(str: string): string {
  return str.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase();
}

function documentMatchesNameFilter(doc: AppDocument, filter: string): boolean {
  const q = normalizeDocumentNameForFilter(filter);
  if (!q) return true;
  return normalizeDocumentNameForFilter(doc.label || doc.name || '').includes(q);
}

const drawerScrollbar = (theme: Theme) => ({
  '&::-webkit-scrollbar': { width: 8, height: 8 },
  '&::-webkit-scrollbar-track': {
    backgroundColor: alpha(theme.palette.common.black, 0.2),
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.common.white, 0.22),
    borderRadius: 4,
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.32),
    },
  },
});

const StyledDrawer = styled(Drawer)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const paperChrome = isDark
    ? {
        color: theme.palette.text.primary,
        background: `linear-gradient(195deg, ${theme.palette.background.paper} 0%, ${alpha('#182433', 1)} 48%, ${alpha('#1c2838', 1)} 100%)`,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxShadow: `${theme.shadows[12]}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.05)}`,
      }
    : {
        color: theme.palette.common.white,
        background: `linear-gradient(165deg, ${alpha(theme.palette.grey[900], 0.98)} 0%, ${alpha('#0f172a', 1)} 52%, ${alpha(theme.palette.grey[900], 0.99)} 100%)`,
        borderRight: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        boxShadow: `${theme.shadows[6]}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`,
      };
  return {
  width: '100%',
  flex: 1,
  minHeight: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '&.MuiDrawer-docked': {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  '& .MuiDrawer-paper': {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1, 0.75),
    marginTop: 0,
    flex: 1,
    minHeight: 0,
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    ...paperChrome,
  },
};
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 400,
    borderRadius: theme.shape.borderRadius,
  },
}));

const DocumentButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 2.5,
  borderLeft: `3px solid transparent`,
  boxSizing: 'border-box',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.action.hover : alpha(theme.palette.common.white, 0.06),
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.22),
    borderLeftColor: theme.palette.primary.light,
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.28),
    },
  },
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const DocumentIcon = styled(ListItemIcon)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark' ? theme.palette.text.secondary : alpha(theme.palette.common.white, 0.88),
  minWidth: 40,
  cursor: 'pointer',
  transition: theme.transitions.create('color', { duration: theme.transitions.duration.shorter }),
  '&:hover': {
    color: theme.palette.primary.light,
  },
}));

const DocumentText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.common.white,
    fontSize: 'calc(var(--app-font-size, 14px) - 2px)',
  },
  '& .MuiListItemText-primary.MuiTypography-root': {
    fontWeight: 600,
  },
  '& .MuiListItemText-secondary': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.text.secondary
        : alpha(theme.palette.common.white, 0.72),
    fontSize: 'calc(var(--app-font-size, 14px) - 4px)',
  },
  flex: 1,
  cursor: 'pointer',
}));

const DocumentList = styled(List)({
  marginTop: 0,
  padding: 0,
  flexShrink: 1,
  overflowY: 'auto',
  maxHeight: '100%',
});

const sectionCard = (theme: Theme, accent: 'success' | 'default' | 'primary') => {
  const isDark = theme.palette.mode === 'dark';
  const accentBg =
    accent === 'success'
      ? alpha(theme.palette.success.main, isDark ? 0.16 : 0.12)
      : accent === 'primary'
        ? alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1)
        : isDark
          ? alpha(theme.palette.common.black, 0.22)
          : alpha(theme.palette.common.white, 0.04);
  const accentBorder =
    accent === 'success'
      ? alpha(theme.palette.success.main, isDark ? 0.35 : 0.28)
      : accent === 'primary'
        ? alpha(theme.palette.primary.main, isDark ? 0.28 : 0.22)
        : isDark
          ? alpha(theme.palette.divider, 0.35)
          : alpha(theme.palette.common.white, 0.1);
  return {
    backgroundColor: accentBg,
    border: `1px solid ${accentBorder}`,
    borderRadius: 12,
    margin: theme.spacing(0.5, 0.5),
    padding: theme.spacing(0.5),
  };
};

const OpenDocumentsSection = styled(Box)(({ theme }) => ({
  ...sectionCard(theme, 'success'),
}));

const ClosedDocumentsSection = styled(Box)(({ theme }) => ({
  ...sectionCard(theme, 'default'),
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const SharedWithMeSection = styled(Box)(({ theme }) => ({
  ...sectionCard(theme, 'primary'),
  flexShrink: 0,
}));

const SharedByMeSection = styled(Box)(({ theme }) => ({
  ...sectionCard(theme, 'default'),
  flexShrink: 0,
}));

function formatPendingOutgoingShareSecondary(inv: DocumentShareInvitation): string {
  const toUser = inv.targetUsername ? `To ${inv.targetUsername}` : 'To user';
  return `${toUser} · Pending`;
}

const MenuSection = styled(Box)(({ theme }) => ({
  ...sectionCard(theme, 'primary'),
  flexShrink: 0,
}));

const CompactTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : alpha(theme.palette.common.white, 0.92),
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  padding: theme.spacing(1, 1.5),
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderBottom: 'none',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const MenuButton = styled(Button)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
  width: '100%',
  justifyContent: 'space-between',
  padding: theme.spacing(1.25, 2),
  backgroundColor: alpha(theme.palette.common.white, 0.06),
  border: `1px solid ${
    isDark ? theme.palette.divider : alpha(theme.palette.common.white, 0.12)
  }`,
  borderRadius: 10,
  margin: 0,
  color: isDark ? theme.palette.text.primary : theme.palette.common.white,
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: isDark ? theme.palette.action.hover : alpha(theme.palette.common.white, 0.1),
    borderColor: isDark ? theme.palette.divider : alpha(theme.palette.common.white, 0.2),
    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.25)}`,
  },
  '&.Mui-selected': {
    backgroundColor: isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.white, 0.12),
    borderColor: isDark ? theme.palette.divider : alpha(theme.palette.common.white, 0.28),
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
  '& .MuiButton-endIcon': {
    marginLeft: theme.spacing(1),
    transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
  },
  '&.menu-open .MuiButton-endIcon': {
    transform: 'rotate(180deg)',
  },
};
});

/** Icon-only row action styled as a bordered button (distinct from the plain document icon on the left). */
const ShareRowIconButton = styled(IconButton)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    flexShrink: 0,
    width: 30,
    height: 30,
    padding: theme.spacing(0.5),
    borderRadius: 8,
    border: `1px solid ${
      isDark ? alpha(theme.palette.common.white, 0.32) : alpha(theme.palette.common.white, 0.4)
    }`,
    backgroundColor: isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.white, 0.1),
    color: isDark ? theme.palette.text.primary : alpha(theme.palette.common.white, 0.92),
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.common.black, isDark ? 0.15 : 0.08)}`,
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      backgroundColor: isDark ? alpha(theme.palette.common.white, 0.14) : alpha(theme.palette.common.white, 0.16),
      borderColor: isDark ? alpha(theme.palette.common.white, 0.5) : alpha(theme.palette.common.white, 0.58),
    },
    '&.Mui-disabled': {
      borderColor: isDark ? alpha(theme.palette.common.white, 0.14) : alpha(theme.palette.common.white, 0.22),
      backgroundColor: isDark ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.common.white, 0.05),
      color: isDark ? theme.palette.text.disabled : alpha(theme.palette.common.white, 0.45),
    },
  };
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: 32,
    padding: theme.spacing(0, 1),
    '&.Mui-expanded': {
      minHeight: 32,
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(0.5, 0),
    '&.Mui-expanded': {
      margin: theme.spacing(0.5, 0),
    },
  },
  '& .MuiAccordionDetails-root': {
    padding: theme.spacing(0.5, 0),
  },
}));

/** Lets an expanded accordion body fill remaining sidebar height (MUI Collapse defaults to content height). */
const accordionFillRemainingHeightSx = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  flex: 1,
  minHeight: 0,
  '&.Mui-expanded': {
    flex: 1,
    minHeight: 0,
  },
  '& .MuiAccordionSummary-root': {
    flexShrink: 0,
  },
  '& .MuiCollapse-root': {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiCollapse-wrapper': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    '&.MuiCollapse-entered': {
      height: '100% !important',
    },
  },
  '& .MuiCollapse-wrapperInner': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
} as const;

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface DocumentPanelProps {
  onNewDocument: (documentName: string, open: boolean) => Promise<any>;
  documents: AppDocument[];
  onOpenDocument: (
    document: AppDocument,
    options?: { forceExpandRoot?: boolean },
  ) => Promise<void>;
  /** Awaitable open that resolves only after the category tree is loaded (AI apply flows). */
  onOpenDocumentFully?: (
    document: AppDocument,
    options?: { forceExpandRoot?: boolean },
  ) => Promise<void>;
  onCloseDocument: (document: AppDocument) => void;
  onDeleteDocument: (documentId: string) => void;
  onRenameDocument: (documentId: string, newName: string) => void;
  onDuplicateDocument: (
    documentId: string,
    newName: string,
    selectedSubcases: string[],
    sourceReadAccessGrantId?: number | null,
  ) => void;
  onMergeDocument: (
    documentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    newName: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  onCompareDocuments: (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  onSelectiveMerge: (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    newName: string,
    baseline: SelectiveMergeBaseline,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  onSelectDocument: (document?: AppDocument) => void;
  onLoadFromFile: (fileName: string, file?: File) => void;
  onExportDocument: (documentId: string) => void;
  currentTab: string | null;
  currentDocument: AppDocument | undefined;
  setGlobalLoading: (loading: boolean) => void;
  tabs: Tab[];
  refreshCurrentDocument?: () => Promise<void>;
  refreshDocuments?: () => Promise<void>;
  documentLastUsedAt?: Record<string, number>;
  onIncomingSharesChanged?: (pendingInvitations: DocumentShareInvitation[]) => void;
  onOutgoingSharesChanged?: (invitations: DocumentShareInvitation[]) => void;
  incomingSharesRefreshToken?: number;
  outgoingSharesRefreshToken?: number;
  sharedWithMeFocusToken?: number;
  /** Rejects when the share cannot be opened (e.g. revoked). */
  onOpenReadOnlyDocument?: (grant: DocumentReadAccessGrant) => Promise<void>;
  /** Parent stores remover for compare / other flows outside this panel. */
  onRegisterReadAccessGrantRemover?: (remove: (grantId: number) => void) => void;
  /** Parent removes revoked/cancelled copy-share invitations from the panel list. */
  onRegisterIncomingShareRemover?: (remove: (invitationId: number) => void) => void;
}

const WORD_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const WORD_XML_PART = /(?:^|\/)word\/(document\.xml|header\d*\.xml|footer\d*\.xml|footnotes\.xml|endnotes\.xml)$/i;
const WORD_REDACTION_BY_LANGUAGE: Record<string, string> = {
  en: '[Redacted]',
  he: '[מצונזר]',
  iw: '[מצונזר]',
  ar: '[محذوف]',
  fr: '[Caviardé]',
  de: '[Geschwärzt]',
  es: '[Censurado]',
  it: '[Oscurato]',
  pt: '[Redigido]',
  nl: '[Geredigeerd]',
  pl: '[Utajniono]',
  ru: '[Скрыто]',
  uk: '[Приховано]',
  tr: '[Redakte]',
  el: '[Λογοκριμένο]',
  zh: '[已编辑]',
  ja: '[墨消し]',
  ko: '[편집됨]',
};

function wordRedactionToken(lang: string): string {
  return WORD_REDACTION_BY_LANGUAGE[lang] || WORD_REDACTION_BY_LANGUAGE.en;
}

function normalizeWordLang(code: string): string {
  const primary = code.trim().toLowerCase().replace('_', '-').split('-')[0];
  if (primary === 'iw') return 'he';
  return primary;
}

function detectWordDocumentLanguage(xmlParts: string[]): string {
  let text = '';
  const langCounts: Record<string, number> = {};
  for (const xml of xmlParts) {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) continue;
    for (const t of Array.from(doc.getElementsByTagNameNS(WORD_NS, 't'))) {
      text += t.textContent || '';
    }
    for (const langEl of Array.from(doc.getElementsByTagNameNS(WORD_NS, 'lang'))) {
      const raw = wordAttr(langEl, 'val') || wordAttr(langEl, 'bidi');
      if (!raw) continue;
      const code = normalizeWordLang(raw);
      if (!code) continue;
      langCounts[code] = (langCounts[code] || 0) + 1;
    }
  }
  if (/[\u0590-\u05FF]/.test(text)) return 'he';
  if (/[\u0600-\u06FF]/.test(text)) return 'ar';
  if (/[\u0400-\u04FF]/.test(text)) {
    return langCounts.uk >= (langCounts.ru || 0) && langCounts.uk > 0 ? 'uk' : 'ru';
  }
  if (/[\u0370-\u03FF]/.test(text)) return 'el';
  if (/[\u3040-\u30FF]/.test(text)) return 'ja';
  if (/[\uAC00-\uD7AF]/.test(text)) return 'ko';
  if (/[\u4E00-\u9FFF]/.test(text)) return 'zh';
  let best = 'en';
  let bestCount = 0;
  for (const [code, count] of Object.entries(langCounts)) {
    if (count > bestCount) {
      best = code;
      bestCount = count;
    }
  }
  return bestCount > 0 ? best : 'en';
}

function wordAttr(el: Element, localName: string): string | null {
  return el.getAttributeNS(WORD_NS, localName) || el.getAttribute(`w:${localName}`) || el.getAttribute(localName);
}

function isNearBlackFill(fill: string): boolean {
  const hex = fill.trim();
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b <= 40;
}

function isWordRunBlackBackground(run: Element): boolean {
  const rPr = Array.from(run.children).find((c) => c.localName === 'rPr');
  if (!rPr) return false;
  for (const child of Array.from(rPr.children)) {
    if (child.localName === 'highlight' && (wordAttr(child, 'val') || '').toLowerCase() === 'black') {
      return true;
    }
    if (child.localName === 'shd') {
      const fill = wordAttr(child, 'fill');
      if (fill && isNearBlackFill(fill)) return true;
    }
  }
  return false;
}

function runTextNodes(run: Element): Element[] {
  return Array.from(run.children).filter((c) => c.localName === 't');
}

function paragraphRunsInOrder(paragraph: Element): Element[] {
  return Array.from(paragraph.getElementsByTagNameNS(WORD_NS, 'r')).filter((run) => {
    let parent = run.parentElement;
    while (parent && parent !== paragraph) {
      if (parent.localName === 'r' && parent.namespaceURI === WORD_NS) return false;
      parent = parent.parentElement;
    }
    return true;
  });
}

function applyWordBlackBackgroundRedaction(xml: string, token: string): string {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return xml;
  let changed = false;
  const paragraphs = Array.from(doc.getElementsByTagNameNS(WORD_NS, 'p'));
  for (const paragraph of paragraphs) {
    let prevRedacted = false;
    for (const run of paragraphRunsInOrder(paragraph)) {
      if (!isWordRunBlackBackground(run)) {
        prevRedacted = false;
        continue;
      }
      const texts = runTextNodes(run);
      if (texts.length === 0) {
        prevRedacted = true;
        continue;
      }
      texts.forEach((t, i) => {
        const next = prevRedacted || i > 0 ? '' : token;
        if (t.textContent !== next) {
          t.textContent = next;
          changed = true;
        }
      });
      prevRedacted = true;
    }
  }
  if (!changed) return xml;
  const serialized = new XMLSerializer().serializeToString(doc);
  const decl = xml.match(/^<\?xml[^?]*\?>\s*/);
  const withoutDecl = serialized.replace(/^<\?xml[^?]*\?>\s*/, '');
  return `${decl ? decl[0] : ''}${withoutDecl}`;
}

async function redactBlackBackgroundInDocx(arrayBuffer: ArrayBuffer): Promise<ArrayBuffer> {
  try {
    const zip = await JSZip.loadAsync(arrayBuffer);
    let changed = false;
    const paths = Object.keys(zip.files).filter((path) => !zip.files[path].dir && WORD_XML_PART.test(path.replace(/\\/g, '/')));
    const xmlByPath: { path: string; xml: string }[] = [];
    for (const path of paths) {
      const file = zip.file(path);
      if (!file) continue;
      xmlByPath.push({ path, xml: await file.async('string') });
    }
    const token = wordRedactionToken(detectWordDocumentLanguage(xmlByPath.map((p) => p.xml)));
    for (const { path, xml } of xmlByPath) {
      const redacted = applyWordBlackBackgroundRedaction(xml, token);
      if (redacted !== xml) {
        zip.file(path, redacted);
        changed = true;
      }
    }
    if (!changed) return arrayBuffer;
    return await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
  } catch (err) {
    console.warn('Could not redact black backgrounds in Word file; importing original text', err);
    return arrayBuffer;
  }
}

const DocumentPanel: React.FC<DocumentPanelProps> = ({
  onNewDocument,
  documents,
  onOpenDocument,
  onOpenDocumentFully,
  onCloseDocument,
  onDeleteDocument,
  onRenameDocument,
  onDuplicateDocument,
  onMergeDocument,
  onCompareDocuments,
  onSelectiveMerge,
  onSelectDocument,
  onLoadFromFile,
  onExportDocument,
  currentTab,
  currentDocument,
  setGlobalLoading,
  tabs,
  refreshCurrentDocument,
  refreshDocuments,
  documentLastUsedAt = {},
  onIncomingSharesChanged,
  onOutgoingSharesChanged,
  incomingSharesRefreshToken = 0,
  outgoingSharesRefreshToken = 0,
  sharedWithMeFocusToken = 0,
  onOpenReadOnlyDocument,
  onRegisterReadAccessGrantRemover,
  onRegisterIncomingShareRemover,
}) => {
  const { suggestTreeFromText, createDocument, expandTreeFromText } = useDocument();
  const { isAuthenticated } = useAuth();
  const { getTooltipTextForState, getMenuTooltipTextForState, helpMode, shouldShowTooltip } = useHelpContent();
  const { isEnabled } = useFeatureFlags();
  const documentSharingEnabled = isEnabled(FEATURE_FLAG_KEYS.DOCUMENT_SHARING);
  const documentMergeCompareEnabled = isEnabled(FEATURE_FLAG_KEYS.DOCUMENT_MERGE_COMPARE);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [openMergeDialog, setOpenMergeDialog] = useState(false);
  const [mergeDialogMode, setMergeDialogMode] = useState<'merge' | 'compare'>('merge');
  const [mergeTargetDocument, setMergeTargetDocument] = useState<AppDocument | undefined>();
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openSuggestTreeDialog, setOpenSuggestTreeDialog] = useState(false);
  const [openExpandTreeDialog, setOpenExpandTreeDialog] = useState(false);
  const [openExpandTreeResultsDialog, setOpenExpandTreeResultsDialog] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [documentToDuplicate, setDocumentToDuplicate] = useState<AppDocument | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [suggestedTree, setSuggestedTree] = useState<any>(null);
  const [expansionResult, setExpansionResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [documentContextMenu, setDocumentContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    document: AppDocument;
  } | null>(null);
  const [grantContextMenu, setGrantContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    grant: DocumentReadAccessGrant;
  } | null>(null);
  const [mergeFromPreset, setMergeFromPreset] = useState<string | undefined>(undefined);
  const contextMenuDocument = documentContextMenu?.document ?? null;
  const [openDocumentsExpanded, setOpenDocumentsExpanded] = useState(true);
  const [closedDocumentsExpanded, setClosedDocumentsExpanded] = useState(true);
  const [closedDocumentsSort, setClosedDocumentsSort] = useState<ClosedDocumentsSortMode>(loadClosedDocumentsSort);
  const [closedDocumentsNameFilter, setClosedDocumentsNameFilter] = useState('');
  const documentsPanelRef = useRef<HTMLDivElement>(null);
  const closedDocumentsSectionRef = useRef<HTMLDivElement>(null);
  const [closedDocumentsListMaxHeight, setClosedDocumentsListMaxHeight] = useState(200);
  const [incomingShares, setIncomingShares] = useState<DocumentShareInvitation[]>([]);
  const [outgoingShares, setOutgoingShares] = useState<DocumentShareInvitation[]>([]);
  const [sharedWithMeExpanded, setSharedWithMeExpanded] = useState(true);
  const [sharedByMeExpanded, setSharedByMeExpanded] = useState(true);
  const [revokingShareId, setRevokingShareId] = useState<number | null>(null);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<AppDocument | null>(null);
  const [openAcceptShareDialog, setOpenAcceptShareDialog] = useState(false);
  const [invitationToAccept, setInvitationToAccept] = useState<DocumentShareInvitation | null>(null);
  const [readAccessWithMe, setReadAccessWithMe] = useState<DocumentReadAccessGrant[]>([]);
  const dismissedReadAccessGrantIdsRef = useRef<Set<number>>(new Set());
  const [readAccessByMe, setReadAccessByMe] = useState<DocumentReadAccessGrant[]>([]);
  const [viewOnlyWithMeExpanded, setViewOnlyWithMeExpanded] = useState(true);
  const [viewOnlyByMeExpanded, setViewOnlyByMeExpanded] = useState(true);
  const [revokingReadAccessId, setRevokingReadAccessId] = useState<number | null>(null);

  const canMergeDocuments =
    documents.length >= 1 &&
    (documents.length >= 2 || readAccessWithMe.length > 0);
  const canCompareDocuments =
    documents.length >= 1 && documents.length + readAccessWithMe.length >= 2;

  const loadShareInvitations = useCallback(async () => {
    try {
      const shareListConfig = skipAuthRedirectConfig();
      const [incomingRes, outgoingRes] = await Promise.all([
        api.get<DocumentShareInvitation[]>('/documents/shares/incoming', shareListConfig),
        api.get<DocumentShareInvitation[]>('/documents/shares/outgoing', shareListConfig),
      ]);
      const incoming = filterPendingIncomingShares(incomingRes.data ?? []);
      setIncomingShares(incoming);
      const outgoing = outgoingRes.data ?? [];
      setOutgoingShares(outgoing);
      onIncomingSharesChanged?.(incoming);
      onOutgoingSharesChanged?.(outgoing);
    } catch (err) {
      console.error('Failed to load document shares:', err);
    }
  }, [onIncomingSharesChanged, onOutgoingSharesChanged]);

  const loadReadAccessGrants = useCallback(async () => {
    try {
      const readAccessListConfig = skipAuthRedirectConfig();
      const [withMeRes, byMeRes] = await Promise.all([
        api.get<DocumentReadAccessGrant[]>('/documents/read-access/shared-with-me', readAccessListConfig),
        api.get<DocumentReadAccessGrant[]>('/documents/read-access/shared-by-me', readAccessListConfig),
      ]);
      const activeWithMe = filterActiveReadAccessGrants(withMeRes.data ?? []).filter(
        (g) => !dismissedReadAccessGrantIdsRef.current.has(g.id),
      );
      setReadAccessWithMe(activeWithMe);
      setReadAccessByMe(filterActiveReadAccessGrants(byMeRes.data ?? []));
    } catch (err) {
      console.error('Failed to load read-only document access:', err);
    }
  }, []);

  const loadSharingState = useCallback(async () => {
    await Promise.all([loadShareInvitations(), loadReadAccessGrants()]);
  }, [loadShareInvitations, loadReadAccessGrants]);

  useEffect(() => {
    if (!isAuthenticated || !documentSharingEnabled) {
      setIncomingShares([]);
      setOutgoingShares([]);
      setReadAccessWithMe([]);
      setReadAccessByMe([]);
      return;
    }
    void loadSharingState();
  }, [isAuthenticated, documentSharingEnabled, loadSharingState]);

  const pendingOutgoingShares = useMemo(
    () => filterPendingOutgoingShares(outgoingShares),
    [outgoingShares],
  );

  useEffect(() => {
    if (sharedWithMeFocusToken > 0) {
      setSharedWithMeExpanded(true);
      setViewOnlyWithMeExpanded(true);
    }
  }, [sharedWithMeFocusToken]);

  useEffect(() => {
    if (incomingSharesRefreshToken > 0 || outgoingSharesRefreshToken > 0) {
      void loadSharingState();
    }
  }, [incomingSharesRefreshToken, outgoingSharesRefreshToken, loadSharingState]);

  const removeReadAccessGrantFromList = useCallback((grantId: number) => {
    dismissedReadAccessGrantIdsRef.current.add(grantId);
    setReadAccessWithMe((prev) => prev.filter((g) => g.id !== grantId));
  }, []);

  const removeIncomingShareFromList = useCallback(
    (invitationId: number) => {
      setIncomingShares((prev) => {
        const next = prev.filter((inv) => inv.id !== invitationId);
        onIncomingSharesChanged?.(next);
        return next;
      });
    },
    [onIncomingSharesChanged],
  );

  const openReadOnlySharedDocument = useCallback(
    async (grant: DocumentReadAccessGrant) => {
      if (!onOpenReadOnlyDocument) {
        return;
      }
      try {
        await onOpenReadOnlyDocument(grant);
      } catch (error) {
        if (shouldRemoveReadAccessGrantAfterOpenError(error)) {
          removeReadAccessGrantFromList(grant.id);
        }
      }
    },
    [onOpenReadOnlyDocument, removeReadAccessGrantFromList],
  );

  useEffect(() => {
    if (!onRegisterReadAccessGrantRemover) {
      return;
    }
    onRegisterReadAccessGrantRemover(removeReadAccessGrantFromList);
  }, [onRegisterReadAccessGrantRemover, removeReadAccessGrantFromList]);

  useEffect(() => {
    if (!onRegisterIncomingShareRemover) {
      return;
    }
    onRegisterIncomingShareRemover(removeIncomingShareFromList);
  }, [onRegisterIncomingShareRemover, removeIncomingShareFromList]);

  const handleRevokeReadAccess = async (grantId: number) => {
    setRevokingReadAccessId(grantId);
    try {
      await api.delete(`/documents/read-access/${grantId}`);
      await loadSharingState();
    } catch (err) {
      console.error('Failed to revoke read-only access:', err);
    } finally {
      setRevokingReadAccessId(null);
    }
  };

  const handleRevokeShare = async (invitationId: number) => {
    setRevokingShareId(invitationId);
    try {
      await api.delete(`/documents/shares/${invitationId}`);
      await loadSharingState();
    } catch (err) {
      console.error('Failed to revoke document share:', err);
    } finally {
      setRevokingShareId(null);
    }
  };

  const closedDocumentsAll = useMemo(
    () => documents.filter((doc) => !doc.hasOpenTab),
    [documents],
  );

  const closedDocumentsFiltered = useMemo(
    () =>
      closedDocumentsAll
        .filter((doc) => documentMatchesNameFilter(doc, closedDocumentsNameFilter))
        .sort((a, b) => compareClosedDocuments(a, b, closedDocumentsSort, documentLastUsedAt)),
    [closedDocumentsAll, closedDocumentsNameFilter, closedDocumentsSort, documentLastUsedAt],
  );

  const closedDocumentsCountLabel = useMemo(() => {
    const total = closedDocumentsAll.length;
    const filterActive = closedDocumentsNameFilter.trim().length > 0;
    if (filterActive) {
      return `Closed Documents (${closedDocumentsFiltered.length}/${total})`;
    }
    return `Closed Documents (${total})`;
  }, [closedDocumentsAll.length, closedDocumentsFiltered.length, closedDocumentsNameFilter]);

  useLayoutEffect(() => {
    const panel = documentsPanelRef.current;
    if (!panel || !closedDocumentsExpanded) {
      return;
    }

    const measure = () => {
      const closedSection = closedDocumentsSectionRef.current;
      if (!closedSection) {
        return;
      }
      const openSection = panel.querySelector(
        '[data-open-documents-section]',
      ) as HTMLElement | null;
      const summary = closedSection.querySelector(
        '.MuiAccordionSummary-root',
      ) as HTMLElement | null;
      const filter = closedSection.querySelector(
        '[aria-label="Filter closed documents by name"]',
      ) as HTMLElement | null;
      const openHeight =
        openDocumentsExpanded && openSection ? openSection.offsetHeight : 0;
      const summaryHeight = summary?.offsetHeight ?? 36;
      const filterHeight = filter?.offsetHeight ?? 40;
      const available =
        panel.clientHeight - openHeight - summaryHeight - filterHeight - 12;
      setClosedDocumentsListMaxHeight(Math.max(80, available));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    const openSection = panel.querySelector(
      '[data-open-documents-section]',
    ) as HTMLElement | null;
    if (openSection) {
      observer.observe(openSection);
    }
    const closedSection = closedDocumentsSectionRef.current;
    if (closedSection) {
      observer.observe(closedSection);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [
    closedDocumentsExpanded,
    documents.length,
    openDocumentsExpanded,
    documentSharingEnabled,
    incomingShares.length,
    outgoingShares.length,
    readAccessWithMe.length,
    readAccessByMe.length,
    sharedWithMeExpanded,
    sharedByMeExpanded,
    viewOnlyWithMeExpanded,
    viewOnlyByMeExpanded,
  ]);

  const handleClosedDocumentsSortToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    const next: ClosedDocumentsSortMode = closedDocumentsSort === 'name' ? 'recent' : 'name';
    setClosedDocumentsSort(next);
    saveClosedDocumentsSort(next);
  };

  const closedDocumentsSortTooltip =
    closedDocumentsSort === 'name'
      ? 'Sorted by name. Click to sort by recently used.'
      : 'Sorted by recently used. Click to sort by name.';

  const [documentToRename, setDocumentToRename] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [suggestTreeFiles, setSuggestTreeFiles] = useState<File[]>([]);
  const [suggestTreeFileNames, setSuggestTreeFileNames] = useState<string[]>([]);
  const [suggestTreeContext, setSuggestTreeContext] = useState('');
  const [suggestTreeStrategy, setSuggestTreeStrategy] = useState<'top_down' | 'bottom_up'>('bottom_up');
  const [openContextDialog, setOpenContextDialog] = useState(false);
  const [pendingContextFiles, setPendingContextFiles] = useState<File[]>([]);
  const [pendingContextFileNames, setPendingContextFileNames] = useState<string[]>([]);
  const [suggestTreeMaxDepth, setSuggestTreeMaxDepth] = useState<number>(1); // Track maxDepth from context dialog
  const [suggestTreeCharsPerSegment, setSuggestTreeCharsPerSegment] = useState<number>(800); // Track chars-per-section from context dialog (for retry)
  const [suggestTreeImportMode, setSuggestTreeImportMode] = useState<'category_tree' | 'content_only'>('category_tree');
  const [expandTreeFiles, setExpandTreeFiles] = useState<File[]>([]);
  const [expandTreeFileNames, setExpandTreeFileNames] = useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const suggestTreeFileInputRef = React.useRef<HTMLInputElement>(null);
  const expandTreeFileInputRef = React.useRef<HTMLInputElement>(null);

  const normalize = (str: string) =>
    str.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase();

  const isDuplicateName = (name: string, excludeId?: string) => {
    const normalizedName = normalize(name);
    return documents.some((doc) => {
      const docName = normalize(doc.name || doc.label || '');
      return docName === normalizedName && (!excludeId || doc.id !== excludeId);
    });
  };

  const handleNameChange = (value: string, operation: 'new' | 'rename' | 'duplicate') => {
    const trimmedValue = value.trim();
    const excludeId = (operation === 'rename' && documentToRename) ? documentToRename :
                      (operation === 'duplicate' && documentToDuplicate) ? documentToDuplicate.id : undefined;
    if (isDuplicateName(trimmedValue, excludeId)) {
      setNameError('A document with this name already exists');
    } else {
      setNameError('');
    }
    switch (operation) {
      case 'new':
        setNewDocumentName(value);
        break;
      case 'rename':
        setNewName(value);
        break;
      case 'duplicate':
        setDuplicateName(value);
        break;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewDocument = () => {
    setOpenDialog(true);
    handleClose();
  };

  const handleOpenClick = async () => {
       if (currentDocument && !currentDocument.hasOpenTab) {
        await onOpenDocument(currentDocument);
        handleClose();
       }
    };

  const handleCloseClick = () => {
      if (currentDocument && currentDocument.hasOpenTab ) {
         onCloseDocument(currentDocument);
         handleClose();
      }
    };

  const handleDeleteDocument = () => {
    if (documentToDelete) {
      onDeleteDocument(documentToDelete);
      setOpenDeleteDialog(false);
      setDocumentToDelete(null);
      handleClose();
    }
  };

  const handleDeleteClick = () => {
    if (currentDocument) {
      setDocumentToDelete(currentDocument.id);
      setOpenDeleteDialog(true);
    }
    // Don't call handleClose() here - let the dialog handle its own state
    // The menu will close automatically when a menu item is clicked
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewDocumentName('');
    setNameError('');
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleCreateDocument = () => {
    const trimmedName = newDocumentName.trim();
    if (!trimmedName) {
      console.log('Empty name, setting error');
      setNameError('Document name cannot be empty');
      return;
    }
    if (isDuplicateName(trimmedName)) {
      console.log('Duplicate name detected, setting error');
      setNameError('A document with this name already exists');
      return;
    }
    console.log('Creating document:', trimmedName);
    // onNewDocument surfaces its own error to the user (alert) and re-throws; swallow the
    // rejection here so it doesn't bubble up as an uncaught runtime error (e.g. 403 at the limit).
    void onNewDocument(trimmedName, true).catch(() => {});
    handleDialogClose();
  };

  const handleRenameClick = () => {
     if (currentDocument) {
        const document = documents.find(doc => doc.id === currentDocument.id);
        if (document) {
            setDocumentToRename(document.name);
            setNewName(document.name);
            setOpenRenameDialog(true);
        }
     }
    handleClose();
  };

  const handleRenameDocument = (newName: string) => {
    const excludeId = documentToRename ? documentToRename : undefined;
    if (documentToRename) {
      onRenameDocument(documentToRename, newName);
      setOpenRenameDialog(false);
      setDocumentToRename(null);
      setNewName('');
      setNameError('');
      handleClose();
    }
  };

  const handleRenameDialogClose = () => {
    setOpenRenameDialog(false);
    setDocumentToRename(null);
    setNewName('');
    setNameError('');
  };

  const handleDuplicateClick = () => {
    if (!currentDocument) {
      handleClose();
      return;
    }
    const document = currentDocument.readOnly
      ? currentDocument
      : documents.find((doc) => doc.id === currentDocument.id);
    if (document) {
      setDocumentToDuplicate(document);
      setOpenDuplicateDialog(true);
    }
    handleClose();
  };

  const handleShareClick = () => {
    if (currentDocument?.readOnly) {
      handleClose();
      return;
    }
    if (currentDocument) {
      const document = documents.find((doc) => doc.id === currentDocument.id);
      if (document) {
        setDocumentToShare(document);
        setOpenShareDialog(true);
      }
    }
    handleClose();
  };

  const handleDuplicateConfirm = async (newName: string, selectedSubcases: string[]) => {
    if (documentToDuplicate) {
      onDuplicateDocument(
        documentToDuplicate.readOnly ? '' : documentToDuplicate.id,
        newName,
        selectedSubcases,
        documentToDuplicate.readOnlyGrantId ?? null,
      );
      setOpenDuplicateDialog(false);
      setDocumentToDuplicate(null);
    }
  };

  const handleDuplicateDialogClose = () => {
    setOpenDuplicateDialog(false);
    setDocumentToDuplicate(null);
    setDuplicateName('');
    setNameError('');
  };

  const handleLoadFromFileClick = () => {
    if (fileInputRef) {
      fileInputRef.current?.click();
    }
    handleClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file) {
          if (file.name.endsWith('.nbr')) {
            try {
              // 1. Read file bytes
              const arrayBuffer = await file.arrayBuffer();
              const documentName = extractDocumentName(new Uint8Array(arrayBuffer));

              // 2. Check for duplicate names using extracted name
              if (isDuplicateName(documentName)) {
                setUploadFile(file);
                setUploadFileName(documentName);
                setOpenUploadDialog(true);
              } else {
                onLoadFromFile(documentName, file);
              }
            } catch (error) {
              console.error("Error extracting document name:", error);
            }
          } else {
            console.error("Invalid file type. Only .nbr files are allowed.");
          }
        }
      }
    }

    // Reset the input value so the same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const showMergeOrCompareDialog = (dialogMode: 'merge' | 'compare') => {
    if (dialogMode === 'merge' && !canMergeDocuments) {
      return;
    }
    if (dialogMode === 'compare' && !canCompareDocuments) {
      return;
    }
    setMergeDialogMode(dialogMode);
    if (dialogMode === 'compare') {
      setMergeTargetDocument(
        currentDocument && !currentDocument.readOnly ? currentDocument : undefined,
      );
      setMergeFromPreset(undefined);
    } else {
      setMergeTargetDocument(currentDocument);
      setMergeFromPreset(undefined);
    }
    setOpenMergeDialog(true);
    handleClose();
  };

  const handleMergeClick = () => {
    showMergeOrCompareDialog('merge');
  };

  const handleCompareClick = () => {
    showMergeOrCompareDialog('compare');
  };

  const handleMerge = async (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    newName: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => {
    if (!mergeIntoDocumentId.trim()) return;
    if (!mergeFromReadAccessGrantId && !mergeFromDocumentId.trim()) return;
    onMergeDocument(
      mergeIntoDocumentId,
      mergeFromDocumentId,
      mergeFromReadAccessGrantId,
      newName,
      resolveMissing,
      resolveTypeMismatch,
    );
  };

  const handleSelectiveMerge = (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    newName: string,
    baseline: SelectiveMergeBaseline,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => {
    if (!mergeIntoDocumentId.trim()) return;
    if (!mergeFromReadAccessGrantId && !mergeFromDocumentId.trim()) return;
    onSelectiveMerge(
      mergeIntoDocumentId,
      mergeFromDocumentId,
      mergeFromReadAccessGrantId,
      leftLabel,
      rightLabel,
      newName,
      baseline,
      resolveMissing,
      resolveTypeMismatch,
    );
  };

  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
    setUploadFileName('');
    setUploadFile(null);
  };

  const handleUploadDialogConfirm = (newName: string) => {
    if (uploadFile) {
      onLoadFromFile(newName, uploadFile);
    }
    handleUploadDialogClose();
  };

  // Utility to extract text from file
  const extractTextFromFile = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'docx') {
      const arrayBuffer = await redactBlackBackgroundInDocx(await file.arrayBuffer());
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } else if (ext === 'pdf') {
     const arrayBuffer = await file.arrayBuffer();
       const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

       const allText: string[] = [];

       for (let i = 1; i <= pdf.numPages; i++) {
         const page = await pdf.getPage(i);
         const content = await page.getTextContent();

         // Group items into lines based on y coordinate
         const linesMap: { [y: number]: string[] } = {};
         const lineTolerate = 2; // Allow small y diffs for same line

         content.items.forEach((item: any) => {
           if (typeof item.str !== 'string' || item.str.trim().length === 0) return;

           const y = Math.round(item.transform[5] / lineTolerate) * lineTolerate;
           if (!linesMap[y]) linesMap[y] = [];
           linesMap[y].push(item.str);
         });

         const sortedLines = Object.entries(linesMap)
           .sort((a, b) => Number(b[0]) - Number(a[0])) // Top-to-bottom
           .map(([_, strs]) => strs.join(' ').trim())
           .filter(line => line.length > 0);

         // Process lines to preserve structure better
         const processedLines: string[] = [];
         
         for (let j = 0; j < sortedLines.length; j++) {
           const line = sortedLines[j];
           const trimmed = line.trim();
           
           if (trimmed.length === 0) continue;

           // Check if this line might be a heading (all caps, shorter length, or starts with numbers)
           const isLikelyHeading = (
             trimmed === trimmed.toUpperCase() && trimmed.length < 50 ||
             /^\d+\.\s/.test(trimmed) ||
             /^[A-Z][A-Z\s]{2,}$/.test(trimmed) ||
             trimmed.length < 30 && /^[A-Z]/.test(trimmed)
           );

           // Check if this line might be a section break
           const isSectionBreak = (
             trimmed.length < 20 && /^[A-Z\s]+$/.test(trimmed) ||
             /^[IVX]+\./.test(trimmed) ||
             /^[A-Z]\.\s/.test(trimmed)
           );

           if (isLikelyHeading || isSectionBreak) {
             // Add extra spacing for headings to help AI identify structure
             processedLines.push('');
             processedLines.push(trimmed);
             processedLines.push('');
           } else {
             // For regular text, preserve line breaks to maintain paragraph structure
             processedLines.push(trimmed);
           }
         }

         allText.push(...processedLines);
       }

       const finalText = allText.join('\n');
       return finalText;
    } else {
      // Assume text file
      return await file.text();
    }
  };

  const handleSuggestTreeClick = () => {
    if (!isEnabled(FEATURE_FLAG_KEYS.SUGGEST_CATEGORY_TREE_FROM_FILES)) return;
    if (suggestTreeFileInputRef) {
      suggestTreeFileInputRef.current?.click();
    }
    handleClose();
  };

  const handleSuggestTreeFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Convert FileList to array and save for context dialog
      const fileArray = Array.from(files);
      setPendingContextFiles(fileArray);
      setPendingContextFileNames(fileArray.map(file => file.name));
      setOpenContextDialog(true);
    }
    if (suggestTreeFileInputRef.current) {
      suggestTreeFileInputRef.current.value = '';
    }
  };

  const handleSuggestTreeRetry = async (maxDepth: number, treeStrategy: 'top_down' | 'bottom_up' = 'bottom_up') => {
    if (suggestTreeFiles.length > 0) {
      try {
        setGlobalLoading(true);
        setError(null);
        setSuggestTreeStrategy(treeStrategy);
        
        // Extract text from all files
        const textPromises = suggestTreeFiles.map(file => extractTextFromFile(file));
        const texts = await Promise.all(textPromises);
        
        // Combine all texts with file names as separators
        const combinedText = texts.map((text, index) => {
          const fileName = suggestTreeFiles[index].name;
          return `=== FILE: ${fileName} ===\n${text}\n`;
        }).join('\n');
        
        console.log('Combined text for retry:', combinedText);
        
        const contentOnly = suggestTreeImportMode === 'content_only';
        const result = await suggestTreeFromText(
          combinedText,
          maxDepth,
          suggestTreeContext,
          treeStrategy,
          contentOnly,
          suggestTreeCharsPerSegment,
        );
        if (result && result.error) {
          setError(result.error);
          setSuggestedTree(null);
          setOpenSuggestTreeDialog(true);
          return;
        }
        setSuggestedTree(result);
      } catch (err) {
        setError('Failed to process files');
        console.error('Error processing files:', err);
      } finally {
        setGlobalLoading(false);
      }
    }
  };

  const handleSuggestTreeDialogClose = () => {
    setOpenSuggestTreeDialog(false);
    setError(null);
    setSuggestTreeFileNames([]);
  };

  const handleContextDialogClose = () => {
    setOpenContextDialog(false);
    setPendingContextFiles([]);
    setPendingContextFileNames([]);
  };

  const handleContextDialogProceed = async (
    context: string,
    maxDepth: number,
    treeStrategy: 'top_down' | 'bottom_up' = 'bottom_up',
    importMode: 'category_tree' | 'content_only' = 'category_tree',
    charsPerSegment: number = 800,
  ) => {
    try {
      setGlobalLoading(true);
      setError(null);
      
      // Store the maxDepth for use in SuggestedTreeDialog
      setSuggestTreeMaxDepth(maxDepth);
      setSuggestTreeStrategy(treeStrategy);
      setSuggestTreeContext(context);
      setSuggestTreeImportMode(importMode);
      setSuggestTreeCharsPerSegment(charsPerSegment);
      
      // Combine all file texts
      let combinedText = '';
      for (const file of pendingContextFiles) {
        const fileText = await extractTextFromFile(file);
        combinedText += fileText + '\n\n';
      }
      
      const contentOnly = importMode === 'content_only';
      const result = await suggestTreeFromText(
        combinedText,
        maxDepth,
        context,
        treeStrategy,
        contentOnly,
        charsPerSegment,
      );
      
      if (result && result.tree) {
        setSuggestedTree(result);
        setSuggestTreeFileNames(pendingContextFileNames);
        setSuggestTreeFiles(pendingContextFiles);
        setOpenSuggestTreeDialog(true);
        setOpenContextDialog(false);
        setPendingContextFiles([]);
        setPendingContextFileNames([]);
      } else {
        setError('No category structure received from the API');
      }
    } catch (error) {
      console.error('Error suggesting tree:', error);
      setError('Failed to suggest category structure');
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleExpandTreeClick = async () => {
    if (!isEnabled(FEATURE_FLAG_KEYS.EXPAND_CATEGORY_TREE_FROM_FILES)) return;
    if (currentDocument) {
      setExpandTreeFiles([]);
      setExpandTreeFileNames([]);
      
      // Trigger file input directly - depth validation will be handled by backend
      if (expandTreeFileInputRef.current) {
        expandTreeFileInputRef.current.click();
      }
    }
  };

  const handleExpandTreeFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setExpandTreeFiles(files);
      setExpandTreeFileNames(files.map(f => f.name));
      setOpenExpandTreeDialog(true);
      handleClose();
    }
    // Reset the input value so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleExpandTreeProceed = async (
    context: string,
    maxDepth: number,
    treeStrategy: 'top_down' | 'bottom_up',
    importMode: 'category_tree' | 'content_only' = 'category_tree',
    contentTarget?: { mode: 'existing' | 'new_subcategory'; targetCategoryId?: string; newSubcategoryName?: string },
    charsPerSegment: number = 800,
  ) => {
    if (!currentDocument || expandTreeFiles.length === 0) return;
    
    try {
      setGlobalLoading(true);
      setError(null);
      
      // Combine all file texts
      let combinedText = '';
      for (const file of expandTreeFiles) {
        const fileText = await extractTextFromFile(file);
        combinedText += fileText + '\n\n';
      }
      
      // Call the API to expand tree
      const contentOnly = importMode === 'content_only';
      const result = await expandTreeFromText(
        currentDocument.id,
        combinedText,
        maxDepth,
        context,
        treeStrategy,
        contentOnly,
        contentTarget?.mode === 'existing' ? contentTarget.targetCategoryId : undefined,
        contentTarget?.mode === 'new_subcategory' ? contentTarget.newSubcategoryName : undefined,
        charsPerSegment,
      );
      
      if (result) {
        setExpansionResult(result);
        setOpenExpandTreeDialog(false);
        setOpenExpandTreeResultsDialog(true);
      } else {
        setError('No expansion result received from the API');
      }
    } catch (error: any) {
      console.error('Error expanding tree:', error);
      
      // Check if this is a depth validation error
      if (error.message && error.message.includes('Cannot expand document: existing structure has depth')) {
        // Extract depth information from the error message
        const depthMatch = error.message.match(/existing structure has depth (\d+), but you selected max depth (\d+)/);
        if (depthMatch) {
          const existingDepth = parseInt(depthMatch[1]);
          const selectedDepth = parseInt(depthMatch[2]);
          setError(`Depth mismatch: Your document has ${existingDepth} levels, but you selected ${selectedDepth}. Please increase the max depth to at least ${existingDepth}.`);
        } else {
          setError(error.message);
        }
      } else {
        setError('Failed to expand category structure');
      }
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleExpandTreeDialogClose = () => {
    setOpenExpandTreeDialog(false);
    setExpandTreeFiles([]);
    setExpandTreeFileNames([]);
  };

  const handleExpandTreeResultsDialogClose = () => {
    setOpenExpandTreeResultsDialog(false);
    setExpansionResult(null);
  };

  const handleApplyExpansion = async () => {
    // TODO: Implement the logic to apply the expansion results
    // This would involve creating new categories and placing content
    console.log('Applying expansion:', expansionResult);
    setOpenExpandTreeResultsDialog(false);
    setExpansionResult(null);
  };

  // Context menu handlers
  const handleContextMenu = (event: React.MouseEvent, document: AppDocument) => {
    event.preventDefault();
    setDocumentContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      document,
    });
  };

  const handleContextMenuClose = () => {
    setDocumentContextMenu(null);
  };

  const handleContextMenuOpen = () => {
    if (contextMenuDocument) {
      onOpenDocument(contextMenuDocument);
    }
    handleContextMenuClose();
  };

  const handleContextMenuCloseDoc = () => {
    if (contextMenuDocument) {
      onCloseDocument(contextMenuDocument);
    }
    handleContextMenuClose();
  };

  const handleContextMenuDelete = () => {
    if (contextMenuDocument) {
      setDocumentToDelete(contextMenuDocument.id);
      setOpenDeleteDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuRename = () => {
    if (contextMenuDocument) {
      setDocumentToRename(contextMenuDocument.name);
      setNewName(contextMenuDocument.name);
      setOpenRenameDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuDuplicate = () => {
    if (contextMenuDocument) {
      setDocumentToDuplicate(contextMenuDocument);
      setOpenDuplicateDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuShare = () => {
    if (contextMenuDocument) {
      setDocumentToShare(contextMenuDocument);
      setOpenShareDialog(true);
    }
    handleContextMenuClose();
  };

  const handleShareAccepted = async (created: AcceptShareResponse) => {
    await loadSharingState();
    if (refreshDocuments) {
      await refreshDocuments();
    }
    const newDoc: AppDocument = {
      id: created.name,
      name: created.name,
      label: created.name,
      categoryId: created.categoryId,
      subcases: [],
    };
    await onOpenDocument(newDoc);
  };

  const handleContextMenuMerge = () => {
    if (
      contextMenuDocument &&
      canMergeDocuments &&
      !contextMenuDocument.readOnly
    ) {
      setMergeDialogMode('merge');
      setMergeTargetDocument(contextMenuDocument);
      setMergeFromPreset(undefined);
      setOpenMergeDialog(true);
    }
    handleContextMenuClose();
  };

  const canUseAsCompareAnchor = (doc: AppDocument) => !doc.readOnly;

  const openCompareDialog = useCallback(
    (leftDocument: AppDocument | undefined, fromPreset?: string) => {
      if (!documentMergeCompareEnabled || !canCompareDocuments) {
        return;
      }
      setMergeDialogMode('compare');
      setMergeTargetDocument(leftDocument);
      setMergeFromPreset(fromPreset);
      setOpenMergeDialog(true);
    },
    [documentMergeCompareEnabled, canCompareDocuments],
  );

  const handleDocumentButtonDoubleClick = (doc: AppDocument) => {
    void onOpenDocument(doc);
  };

  const handleContextMenuCompare = () => {
    if (contextMenuDocument && canUseAsCompareAnchor(contextMenuDocument)) {
      openCompareDialog(contextMenuDocument);
    } else {
      openCompareDialog(undefined);
    }
    handleContextMenuClose();
  };

  const handleGrantContextMenuCompare = () => {
    const grant = grantContextMenu?.grant;
    if (grant) {
      openCompareDialog(undefined, mergeFromReadAccessGrantValue(grant.id));
    }
    handleGrantContextMenuClose();
  };

  const handleGrantContextMenu = (
    event: React.MouseEvent,
    grant: DocumentReadAccessGrant,
  ) => {
    event.preventDefault();
    setGrantContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      grant,
    });
  };

  const handleGrantContextMenuClose = () => {
    setGrantContextMenu(null);
  };

  const handleGrantContextMenuDuplicate = async () => {
    const grant = grantContextMenu?.grant;
    handleGrantContextMenuClose();
    if (!grant) {
      return;
    }
    try {
      const response = await api.post<{
        grantId: number;
        documentName: string;
        categoryId: string;
        ownerUsername: string;
      }>(`/documents/read-access/${grant.id}/open`);
      const data = response.data;
      const subcasesResponse = await api.get<string[]>('/documents/subcases', {
        params: { documentId: data.categoryId },
      });
      setDocumentToDuplicate({
        id: readOnlyDocumentTabId(grant.id),
        name: data.documentName,
        label: readOnlyDocumentLabel(data.documentName, grant.ownerUsername),
        categoryId: data.categoryId,
        subcases: Array.isArray(subcasesResponse.data) ? subcasesResponse.data : [],
        readOnly: true,
        readOnlyGrantId: grant.id,
        ownerUsername: grant.ownerUsername,
      });
      setOpenDuplicateDialog(true);
    } catch (error) {
      console.error('Failed to prepare duplicate from view-only share', error);
    }
  };

  const handleGrantButtonDoubleClick = (grant: DocumentReadAccessGrant) => {
    void openReadOnlySharedDocument(grant);
  };

  const handleContextMenuExport = () => {
    if (contextMenuDocument) {
      onExportDocument(contextMenuDocument.id);
    }
    handleContextMenuClose();
  };

  const [subCategories, setSubCategories] = useState<string[]>([]);

  useEffect(() => {
    if (!openDuplicateDialog) {
      setSubCategories([]);
      return;
    }

    let cancelled = false;

    const loadSubCategories = async () => {
      if (!documentToDuplicate?.categoryId) {
        setSubCategories([]);
        return;
      }

      try {
        if (!documentToDuplicate.readOnly && documentToDuplicate.name) {
          await api.post('/documents/open', null, {
            params: { documentId: documentToDuplicate.name },
          });
          if (cancelled) return;
        }

        const subcategoriesResponse = await api.get('/categories/subcategories', {
          params: { categoryId: documentToDuplicate.categoryId },
        });
        if (!cancelled) {
          setSubCategories(subcategoriesResponse.data.map((c: Category) => c.name));
        }
      } catch (error) {
        console.error('Failed to load subcategories', error);
        if (!cancelled) {
          setSubCategories([]);
        }
      }
    };

    void loadSubCategories();

    return () => {
      cancelled = true;
    };
  }, [openDuplicateDialog, documentToDuplicate]);

  const documentMenuWritesDisabled = !!currentDocument?.readOnly;

  const rawDocumentMenuItems: Array<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    tooltipKey?: HelpContentKey;
    menuId?: 'export-nbr';
  }> = [
    {
      label: 'New..',
      icon: <AddIcon fontSize="small" />,
      onClick: handleNewDocument,
      tooltipKey: 'new-document' as const
    },
    {
      label: 'Open',
      icon: <ArticleIcon fontSize="small" />,
      onClick: handleOpenClick,
      disabled: !currentDocument || currentDocument.hasOpenTab,
      tooltipKey: 'open-document' as const
    },
    {
      label: 'Close',
      icon: <InsertDriveFileIcon fontSize="small" />,
      onClick: handleCloseClick,
      disabled: !currentDocument || !currentDocument.hasOpenTab,
      tooltipKey: 'close-document' as const
    },
    {
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" />,
      onClick: handleDeleteClick,
      disabled: !currentDocument || documentMenuWritesDisabled,
      tooltipKey: 'delete-document' as const
    },
    {
      label: 'Rename..',
      icon: <DriveFileRenameOutlineIcon fontSize="small" />,
      onClick: handleRenameClick,
      disabled: !currentDocument || documentMenuWritesDisabled,
      tooltipKey: 'rename-document' as const
    },
    {
      label: 'Duplicate..',
      icon: <ContentCopyIcon fontSize="small" />,
      onClick: handleDuplicateClick,
      disabled: !currentDocument,
      tooltipKey: 'duplicate-document' as const
    },
    {
      label: 'Merge..',
      icon: <MergeIcon fontSize="small" />,
      onClick: handleMergeClick,
      disabled: !canMergeDocuments || documentMenuWritesDisabled,
      tooltipKey: 'merge-document' as const
    },
    {
      label: 'Compare..',
      icon: <CompareArrowsIcon fontSize="small" />,
      onClick: handleCompareClick,
      disabled: !canCompareDocuments,
      tooltipKey: 'document-merge-compare-menu' as const
    },
    {
      label: 'Share with user..',
      icon: <ShareIcon fontSize="small" />,
      onClick: handleShareClick,
      disabled: !currentDocument || documentMenuWritesDisabled,
      tooltipKey: 'share-document' as const
    },
    {
      label: 'New document from files (AI)..',
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: handleSuggestTreeClick,
      disabled: documentMenuWritesDisabled,
      tooltipKey: 'suggest-tree' as const
    },
    {
      label: 'Add to document from files (AI)..',
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: handleExpandTreeClick,
      disabled: !currentDocument || documentMenuWritesDisabled,
      tooltipKey: 'expand-document' as const
    },
    {
      label: 'Import .nbr archive..',
      icon: <UploadFileIcon fontSize="small" />,
      onClick: handleLoadFromFileClick,
      tooltipKey: 'load-from-file' as const
    },
    {
      label: 'Export .nbr archive..',
      icon: <DownloadIcon fontSize="small" />,
      onClick: () => {
        if (currentDocument) {
          onExportDocument(currentDocument.id);
        }
      },
      disabled: !currentDocument || documentMenuWritesDisabled,
      menuId: 'export-nbr',
    },
  ];

  const menuItems = rawDocumentMenuItems.filter((item) => {
    if (item.tooltipKey === 'suggest-tree') {
      return isEnabled(FEATURE_FLAG_KEYS.SUGGEST_CATEGORY_TREE_FROM_FILES);
    }
    if (item.tooltipKey === 'expand-document') {
      return isEnabled(FEATURE_FLAG_KEYS.EXPAND_CATEGORY_TREE_FROM_FILES);
    }
    if (item.menuId === 'export-nbr') {
      return isEnabled(FEATURE_FLAG_KEYS.EXPORT_TO_FILE);
    }
    if (item.tooltipKey === 'share-document') {
      return documentSharingEnabled;
    }
    if (item.tooltipKey === 'document-merge-compare-menu') {
      return documentMergeCompareEnabled;
    }
    return true;
  });

  const getMenuItemTooltip = (item: (typeof menuItems)[number]) => {
    if (!shouldShowTooltip() || !item.tooltipKey) return '';
    const base = getTooltipTextForState(item.tooltipKey, !item.disabled);
    const docName = currentDocument?.label || currentDocument?.name || 'Unnamed';

    // In Help Mode we want to keep the detailed tooltip content and only append context.
    if (helpMode) {
      if (!currentDocument) {
        // For actions that require a document selection, append a helpful note.
        const needsDoc = [
          'open-document',
          'close-document',
          'delete-document',
          'rename-document',
          'duplicate-document',
          'share-document',
          'expand-document'
        ].includes(item.tooltipKey);
        return needsDoc ? `${base}\n\nSelect a document in the list first.` : base;
      }
      return `${base}\n\nDocument: ${docName}`;
    }

    // Otherwise, use short, contextual single-line tooltips for document actions
    switch (item.tooltipKey) {
      case 'open-document': {
        if (!currentDocument) return 'Open document — select a document';
        return `Open document ${docName}`;
      }
      case 'close-document': {
        if (!currentDocument) return 'Close document — select a document';
        if (!currentDocument.hasOpenTab) return `Close document ${docName} — not open`;
        return `Close document ${docName}`;
      }
      case 'delete-document': {
        if (!currentDocument) return 'Delete document — select a document';
        return `Delete document ${docName}`;
      }
      case 'rename-document': {
        if (!currentDocument) return 'Rename document — select a document';
        return `Rename document ${docName}`;
      }
      case 'duplicate-document': {
        if (!currentDocument) return 'Duplicate document — select a document';
        return `Duplicate document ${docName}`;
      }
      case 'merge-document': {
        if (!canMergeDocuments) {
          return 'Merge documents — need two sources (two of yours, or one of yours plus a view-only share to merge from)';
        }
        if (!currentDocument) {
          return 'Merge documents — choose target and source in the dialog';
        }
        return `Merge document ${docName}`;
      }
      case 'document-merge-compare-menu': {
        if (!canCompareDocuments) {
          return 'Compare documents — need your own document plus another source';
        }
        if (!currentDocument || currentDocument.readOnly) {
          return 'Compare documents — choose left and right in the dialog';
        }
        return `Compare documents — ${docName}`;
      }
      case 'share-document': {
        if (!currentDocument) return 'Share document — select a document';
        return `Share document ${docName} with another user`;
      }
      case 'load-from-file': {
        return 'Import .nbr archive';
      }
      case 'suggest-tree': {
        return 'New document from files (AI)';
      }
      case 'expand-document': {
        if (!currentDocument) return 'Add to document from files (AI) — select a document';
        return `Add to document from files (AI) — ${docName}`;
      }
      default:
        return base;
    }

  };

  return (
    <StyledDrawer variant="permanent">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
        }}
      >
      <input
        type="file"
        accept=".nbr"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <input
        type="file"
        accept=".pdf,.txt,.docx,.doc,.rtf,.odt,.md,.html,.htm"
        multiple
        style={{ display: 'none' }}
        ref={suggestTreeFileInputRef}
        onChange={handleSuggestTreeFileChange}
      />
      <input
        type="file"
        accept=".pdf,.txt,.docx,.doc,.rtf,.odt,.md,.html,.htm"
        multiple
        style={{ display: 'none' }}
        ref={expandTreeFileInputRef}
        onChange={handleExpandTreeFileChange}
      />
    <MenuSection>
      <Tooltip
        title={getTooltipTextForState('document-menu', true)}
        placement="right"
      >
        <MenuButton
          onClick={handleClick}
          className={Boolean(anchorEl) ? 'menu-open' : ''}
          startIcon={<MenuIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          variant="outlined"
        >
          Document Menu
        </MenuButton>
      </Tooltip>
    </MenuSection>

      {documentSharingEnabled && incomingShares.length > 0 && (
        <>
          <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 1 })} />
          <SharedWithMeSection>
            <StyledAccordion
              expanded={sharedWithMeExpanded}
              onChange={() => setSharedWithMeExpanded(!sharedWithMeExpanded)}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.text.secondary
                          : alpha(theme.palette.common.white, 0.65),
                    })}
                  />
                }
              >
                <CompactTypography>
                  <InboxIcon
                    sx={(theme) => ({
                      fontSize: 16,
                      color: alpha(theme.palette.primary.light, 0.95),
                    })}
                  />
                  Shared with me ({incomingShares.length})
                </CompactTypography>
              </AccordionSummary>
              <AccordionDetails>
                <DocumentList>
                  {incomingShares.map((inv) => (
                    <DocumentButton
                      key={inv.id}
                      onClick={() => {
                        setInvitationToAccept(inv);
                        setOpenAcceptShareDialog(true);
                      }}
                      sx={{ padding: '4px 12px', minHeight: '32px' }}
                    >
                      <DocumentIcon>
                        <InboxIcon />
                      </DocumentIcon>
                      <DocumentText
                        primary={inv.sourceDocumentName}
                        secondary={`From ${inv.sourceUsername}`}
                      />
                    </DocumentButton>
                  ))}
                </DocumentList>
              </AccordionDetails>
            </StyledAccordion>
          </SharedWithMeSection>
        </>
      )}

      {documentSharingEnabled && pendingOutgoingShares.length > 0 && (
        <>
          <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 1 })} />
          <SharedByMeSection>
            <StyledAccordion
              expanded={sharedByMeExpanded}
              onChange={() => setSharedByMeExpanded(!sharedByMeExpanded)}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.text.secondary
                          : alpha(theme.palette.common.white, 0.65),
                    })}
                  />
                }
              >
                <CompactTypography>
                  <OutboxIcon
                    sx={(theme) => ({
                      fontSize: 16,
                      color: alpha(theme.palette.text.secondary, 0.95),
                    })}
                  />
                  Shared by me ({pendingOutgoingShares.length})
                </CompactTypography>
              </AccordionSummary>
              <AccordionDetails>
                <DocumentList>
                  {pendingOutgoingShares.map((inv) => (
                    <DocumentButton
                      key={inv.id}
                      sx={{ padding: '4px 8px 4px 12px', minHeight: '36px' }}
                    >
                      <DocumentIcon sx={{ minWidth: 32 }}>
                        <OutboxIcon fontSize="small" />
                      </DocumentIcon>
                      <DocumentText
                        primary={inv.sourceDocumentName}
                        secondary={formatPendingOutgoingShareSecondary(inv)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          mr: 0.5,
                        }}
                      />
                      <Tooltip title="Cancel pending share invitation">
                        <span>
                          <ShareRowIconButton
                            size="small"
                            disabled={revokingShareId === inv.id}
                            aria-label="Cancel pending share invitation"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRevokeShare(inv.id);
                            }}
                          >
                            {revokingShareId === inv.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <LinkOffIcon sx={{ fontSize: 18 }} />
                            )}
                          </ShareRowIconButton>
                        </span>
                      </Tooltip>
                    </DocumentButton>
                  ))}
                </DocumentList>
              </AccordionDetails>
            </StyledAccordion>
          </SharedByMeSection>
        </>
      )}

      {documentSharingEnabled && readAccessWithMe.length > 0 && (
        <>
          <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 1 })} />
          <SharedWithMeSection>
            <StyledAccordion
              expanded={viewOnlyWithMeExpanded}
              onChange={() => setViewOnlyWithMeExpanded(!viewOnlyWithMeExpanded)}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.text.secondary
                          : alpha(theme.palette.common.white, 0.65),
                    })}
                  />
                }
              >
                <CompactTypography>
                  <VisibilityIcon
                    sx={(theme) => ({
                      fontSize: 16,
                      color: alpha(theme.palette.info.light, 0.95),
                    })}
                  />
                  View only shared with me ({readAccessWithMe.length})
                </CompactTypography>
              </AccordionSummary>
              <AccordionDetails>
                <DocumentList>
                  {readAccessWithMe.map((grant) => (
                    <DocumentButton
                      key={grant.id}
                      onClick={() => {
                        void openReadOnlySharedDocument(grant);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleGrantButtonDoubleClick(grant);
                      }}
                      onContextMenu={(e) => handleGrantContextMenu(e, grant)}
                      sx={{ padding: '4px 12px', minHeight: '32px' }}
                    >
                      <DocumentIcon>
                        <VisibilityIcon />
                      </DocumentIcon>
                      <DocumentText
                        primary={grant.documentName}
                        secondary={`From ${grant.ownerUsername}`}
                      />
                    </DocumentButton>
                  ))}
                </DocumentList>
              </AccordionDetails>
            </StyledAccordion>
          </SharedWithMeSection>
        </>
      )}

      {documentSharingEnabled && readAccessByMe.length > 0 && (
        <>
          <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 1 })} />
          <SharedByMeSection>
            <StyledAccordion
              expanded={viewOnlyByMeExpanded}
              onChange={() => setViewOnlyByMeExpanded(!viewOnlyByMeExpanded)}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.text.secondary
                          : alpha(theme.palette.common.white, 0.65),
                    })}
                  />
                }
              >
                <CompactTypography>
                  <VisibilityIcon
                    sx={(theme) => ({
                      fontSize: 16,
                      color: alpha(theme.palette.info.light, 0.85),
                    })}
                  />
                  View only shared by me ({readAccessByMe.length})
                </CompactTypography>
              </AccordionSummary>
              <AccordionDetails>
                <DocumentList>
                  {readAccessByMe.map((grant) => (
                    <DocumentButton
                      key={grant.id}
                      sx={{ padding: '4px 8px 4px 12px', minHeight: '36px' }}
                    >
                      <DocumentIcon sx={{ minWidth: 32 }}>
                        <VisibilityIcon fontSize="small" />
                      </DocumentIcon>
                      <DocumentText
                        primary={grant.documentName}
                        secondary={
                          grant.viewerUsername
                            ? `View access: ${grant.viewerUsername}`
                            : 'View access granted'
                        }
                        sx={{ flex: 1, minWidth: 0, mr: 0.5 }}
                      />
                      <Tooltip title="Stop view-only access for this user">
                        <span>
                          <ShareRowIconButton
                            size="small"
                            disabled={revokingReadAccessId === grant.id}
                            aria-label="Stop view-only access for this user"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRevokeReadAccess(grant.id);
                            }}
                          >
                            {revokingReadAccessId === grant.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <VisibilityOffIcon sx={{ fontSize: 18 }} />
                            )}
                          </ShareRowIconButton>
                        </span>
                      </Tooltip>
                    </DocumentButton>
                  ))}
                </DocumentList>
              </AccordionDetails>
            </StyledAccordion>
          </SharedByMeSection>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 320,
              width: 320,
              overflowX: 'hidden',
              mt: 0.5,
            },
          },
        }}
      >
        {menuItems.map((item) => {
          const showTooltip = shouldShowTooltip();
          return (
          <Tooltip 
            key={item.label}
            title={getMenuItemTooltip(item)}
            placement="right"
            disableHoverListener={!showTooltip}
            disableFocusListener={!showTooltip}
            disableTouchListener={!showTooltip}
          >
            <span>
              <MenuItem 
                onClick={item.onClick}
                disabled={item.disabled}
                sx={{
                  fontSize: 'var(--app-font-size, 14px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            </span>
          </Tooltip>
          );
        })}
      </Menu>

      <StyledDialog
        open={openDialog}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleDialogClose();
        }}
      >
        <DialogTitle>New Document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDocumentName}
            onChange={(e) => handleNameChange(e.target.value, 'new')}
            error={!!nameError}
            helperText={nameError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleCreateDocument}
            variant="contained"
            color="primary"
            disabled={!newDocumentName.trim() || !!nameError}
          >
            Create
          </Button>
        </DialogActions>
      </StyledDialog>

      <StyledDialog
        open={openDeleteDialog}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleDeleteDialogClose();
        }}
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{documents.find(doc => doc.id === documentToDelete)?.label}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button 
            onClick={handleDeleteDocument}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </StyledDialog>

      <DuplicateDocumentDialog
        open={openDuplicateDialog}
        onClose={() => {
          setOpenDuplicateDialog(false);
          setDocumentToDuplicate(null);
        }}
        onConfirm={handleDuplicateConfirm}
        documentName={documentToDuplicate?.label || ''}
        subcases={documentToDuplicate?.subcases || []}
        existingDocumentNames={documents.map(doc => doc.label)}
        documentId={documentToDuplicate?.categoryId || ''}
        subCategories={subCategories}
        sourceIsReadOnlyShared={!!documentToDuplicate?.readOnly}
      />
      <RenameDocumentDialog
          open={openRenameDialog}
          onClose={handleRenameDialogClose}
          onRename={handleRenameDocument}
          originalName={newName}
          existingNames={documents.map(doc => doc.label)}
          documentName={documentToRename ?? ''}
          categoryId={documents.find((d) => d.name === documentToRename)?.categoryId}
        />
      <MergeDocumentDialog
        open={openMergeDialog}
        mode={mergeDialogMode}
        onClose={() => {
          setOpenMergeDialog(false);
          setMergeDialogMode('merge');
          setMergeTargetDocument(undefined);
          setMergeFromPreset(undefined);
        }}
        onMerge={handleMerge}
        onCompare={onCompareDocuments}
        onSelectiveMerge={handleSelectiveMerge}
        documents={documents}
        sharedReadAccessGrants={readAccessWithMe}
        currentDocument={mergeTargetDocument ?? currentDocument}
        initialMergeFromId={mergeFromPreset}
      />

      {documentSharingEnabled ? (
        <>
          <ShareDocumentDialog
            open={openShareDialog}
            onClose={() => {
              setOpenShareDialog(false);
              setDocumentToShare(null);
            }}
            onShared={() => void loadSharingState()}
            documentName={documentToShare?.name ?? ''}
          />

          <AcceptDocumentShareDialog
            open={openAcceptShareDialog}
            onClose={() => {
              setOpenAcceptShareDialog(false);
              setInvitationToAccept(null);
              void loadSharingState();
            }}
            invitation={invitationToAccept}
            existingDocumentNames={documents.map((doc) => doc.label)}
            onAccepted={handleShareAccepted}
          />
        </>
      ) : null}

      <ChooseDocumentNameDialog
        open={openUploadDialog}
        onClose={handleUploadDialogClose}
        onConfirm={handleUploadDialogConfirm}
        originalName={uploadFileName}
        existingNames={documents.map(doc => doc.label)}
      />

      <StyledDialog
        open={openSuggestTreeDialog}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleSuggestTreeDialogClose();
        }}
      >
        <SuggestedTreeDialog
          open={openSuggestTreeDialog}
          onClose={handleSuggestTreeDialogClose}
          onRetry={handleSuggestTreeRetry}
          suggestedTree={suggestedTree}
          fileNames={suggestTreeFileNames}
          loading={loading}
          onOpenDocument={onOpenDocumentFully ?? onOpenDocument}
          onNewDocument={onNewDocument}
          error={error}
          initialMaxDepth={suggestTreeMaxDepth}
          initialTreeStrategy={suggestTreeStrategy}
          importMode={suggestTreeImportMode}
          setGlobalLoading={setGlobalLoading}
        />
      </StyledDialog>

      <SuggestTreeContextDialog
        open={openContextDialog}
        onClose={handleContextDialogClose}
        onProceed={handleContextDialogProceed}
        fileNames={pendingContextFileNames}
      />

                          <ExpandTreeDialog
              open={openExpandTreeDialog}
              onClose={handleExpandTreeDialogClose}
              onProceed={handleExpandTreeProceed}
              fileNames={expandTreeFileNames}
              currentDocumentName={currentDocument?.label || ''}
              documentCategoryId={currentDocument?.categoryId}
            />

      <ExpandTreeResultsDialog
        open={openExpandTreeResultsDialog}
        onClose={handleExpandTreeResultsDialogClose}
        expansionResult={expansionResult}
        onApplyExpansion={handleApplyExpansion}
        currentDocument={currentDocument}
        existingDocuments={documents}
        onOpenDocument={onOpenDocumentFully ?? onOpenDocument}
        refreshCurrentDocument={refreshCurrentDocument}
        onNewDocument={onNewDocument}
        refreshDocuments={refreshDocuments}
        setGlobalLoading={setGlobalLoading}
      />

      {documents.length > 0 && (
        <>
          <Divider sx={(theme) => ({ borderColor: theme.palette.divider, my: 1 })} />
          <Box
            ref={documentsPanelRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Open Documents Section */}
            <OpenDocumentsSection data-open-documents-section sx={{ flexShrink: 0 }}>
              <StyledAccordion 
                expanded={openDocumentsExpanded}
                onChange={() => setOpenDocumentsExpanded(!openDocumentsExpanded)}
              >
                <AccordionSummary 
                  expandIcon={
                    <ExpandMoreIcon
                      sx={(theme) => ({
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.text.secondary
                            : alpha(theme.palette.common.white, 0.65),
                      })}
                    />
                  }
                >
                  <CompactTypography>
                    <ArticleIcon
                      sx={(theme) => ({
                        fontSize: 16,
                        color: alpha(theme.palette.success.light, 0.95),
                      })}
                    />
                    Open Documents ({documents.filter(doc => doc.hasOpenTab).length})
                  </CompactTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <DocumentList>
                    {[...documents.filter(doc => doc.hasOpenTab)]
                      .sort((a, b) => {
                        const aIdx = tabs.findIndex(tab => tab.id === a.id);
                        const bIdx = tabs.findIndex(tab => tab.id === b.id);
                        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                        if (aIdx !== -1) return -1;
                        if (bIdx !== -1) return 1;
                        return a.label.localeCompare(b.label, undefined, { numeric: true });
                      })
                      .map((doc) => (
                        <DocumentButton
                          key={doc.id}
                          selected={currentDocument && currentDocument.id === doc.id}
                          onClick={() => {
                            if (currentDocument && currentDocument.id === doc.id) {
                              onSelectDocument(undefined);
                            } else {
                              onSelectDocument(doc);
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            handleDocumentButtonDoubleClick(doc);
                          }}
                          onContextMenu={(e) => handleContextMenu(e, doc)}
                          sx={{ padding: '4px 12px', minHeight: '32px' }}
                        >
                          <DocumentIcon onClick={(e) => {
                            e.stopPropagation();
                            if (doc.hasOpenTab) {
                              onCloseDocument(doc);
                            }
                          }}>
                            <ArticleIcon />
                          </DocumentIcon>
                          <DocumentText primary={doc.label} />
                        </DocumentButton>
                      ))}
                  </DocumentList>
                </AccordionDetails>
              </StyledAccordion>
            </OpenDocumentsSection>

            {/* Closed Documents Section */}
            <ClosedDocumentsSection ref={closedDocumentsSectionRef} data-closed-documents-section>
              <StyledAccordion 
                expanded={closedDocumentsExpanded}
                onChange={() => setClosedDocumentsExpanded(!closedDocumentsExpanded)}
                sx={accordionFillRemainingHeightSx}
              >
                <AccordionSummary 
                  expandIcon={
                    <ExpandMoreIcon
                      sx={(theme) => ({
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.text.secondary
                            : alpha(theme.palette.common.white, 0.65),
                      })}
                    />
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minWidth: 0,
                      pr: 0.25,
                    }}
                  >
                    <CompactTypography sx={{ flex: 1, minWidth: 0 }}>
                      <InsertDriveFileIcon
                        sx={(theme) => ({
                          fontSize: 16,
                          color: alpha(theme.palette.grey[400], 0.95),
                        })}
                      />
                      {closedDocumentsCountLabel}
                    </CompactTypography>
                    <Tooltip title={closedDocumentsSortTooltip} placement="top">
                      <IconButton
                        size="small"
                        aria-label={closedDocumentsSortTooltip}
                        onClick={handleClosedDocumentsSortToggle}
                        sx={(theme) => ({
                          flexShrink: 0,
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.text.secondary
                              : alpha(theme.palette.common.white, 0.75),
                        })}
                      >
                        {closedDocumentsSort === 'name' ? (
                          <SortByAlphaIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <HistoryIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    pt: 0.5,
                    pb: 0.5,
                    px: 0,
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Filter by name…"
                    value={closedDocumentsNameFilter}
                    onChange={(e) => setClosedDocumentsNameFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label="Filter closed documents by name"
                    sx={(theme) => ({
                      flexShrink: 0,
                      px: 1,
                      pb: 0.75,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem',
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.text.primary
                            : theme.palette.common.white,
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.black, 0.2)
                            : alpha(theme.palette.common.black, 0.25),
                        '& fieldset': {
                          borderColor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.divider
                              : alpha(theme.palette.common.white, 0.2),
                        },
                        '&:hover fieldset': {
                          borderColor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.text.secondary
                              : alpha(theme.palette.common.white, 0.35),
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        opacity: 0.65,
                      },
                    })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                        </InputAdornment>
                      ),
                      endAdornment: closedDocumentsNameFilter ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear filter"
                            onClick={(e) => {
                              e.stopPropagation();
                              setClosedDocumentsNameFilter('');
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <ClearIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                  <DocumentList
                    sx={(theme) => ({
                      flex: '0 0 auto',
                      maxHeight: closedDocumentsListMaxHeight,
                      overflowY: 'auto',
                      ...drawerScrollbar(theme),
                    })}
                  >
                    {closedDocumentsFiltered.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          display: 'block',
                          px: 1.5,
                          py: 0.5,
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.text.secondary
                              : alpha(theme.palette.common.white, 0.65),
                        })}
                      >
                        {closedDocumentsNameFilter.trim()
                          ? 'No documents match your filter.'
                          : 'No closed documents.'}
                      </Typography>
                    ) : (
                      closedDocumentsFiltered.map((doc) => (
                        <DocumentButton
                          key={doc.id}
                          selected={currentDocument && currentDocument.id === doc.id}
                          onClick={() => {
                            if (currentDocument && currentDocument.id === doc.id) {
                              onSelectDocument(undefined);
                            } else {
                              onSelectDocument(doc);
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            handleDocumentButtonDoubleClick(doc);
                          }}
                          onContextMenu={(e) => handleContextMenu(e, doc)}
                          sx={{ padding: '4px 12px', minHeight: '32px' }}
                        >
                          <DocumentIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!doc.hasOpenTab) {
                                onOpenDocument(doc);
                              }
                            }}
                          >
                            <InsertDriveFileIcon />
                          </DocumentIcon>
                          <DocumentText primary={doc.label} />
                        </DocumentButton>
                      ))
                    )}
                  </DocumentList>
                </AccordionDetails>
              </StyledAccordion>
            </ClosedDocumentsSection>
          </Box>
        </>
      )}

      </Box>

      {/* Context Menu — same anchoring as category tree (cursor position, menu down and to the right) */}
      <Menu
        open={documentContextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          documentContextMenu !== null
            ? { top: documentContextMenu.mouseY, left: documentContextMenu.mouseX }
            : undefined
        }
      >
        <Tooltip title={getMenuTooltipTextForState('open-document', !!(contextMenuDocument && !contextMenuDocument.hasOpenTab))} placement="right">
          <span>
            <MenuItem
              onClick={handleContextMenuOpen}
              disabled={!contextMenuDocument || contextMenuDocument.hasOpenTab}
            >
              <ListItemIcon>
                <ArticleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Open" />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getMenuTooltipTextForState('close-document', !!(contextMenuDocument && contextMenuDocument.hasOpenTab))} placement="right">
          <span>
            <MenuItem
              onClick={handleContextMenuCloseDoc}
              disabled={!contextMenuDocument || !contextMenuDocument.hasOpenTab}
            >
              <ListItemIcon>
                <InsertDriveFileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Close" />
            </MenuItem>
          </span>
        </Tooltip>
        <Divider />
        <Tooltip title={getMenuTooltipTextForState('rename-document', !!contextMenuDocument)} placement="right">
          <span>
            <MenuItem onClick={handleContextMenuRename}>
              <ListItemIcon>
                <DriveFileRenameOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Rename..." />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getMenuTooltipTextForState('duplicate-document', !!contextMenuDocument)} placement="right">
          <span>
            <MenuItem onClick={handleContextMenuDuplicate}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Duplicate..." />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={getMenuTooltipTextForState(
            'merge-document',
            !!(
              contextMenuDocument &&
              canMergeDocuments &&
              !contextMenuDocument.readOnly
            ),
          )}
          placement="right"
        >
          <span>
            <MenuItem
              onClick={handleContextMenuMerge}
              disabled={
                !contextMenuDocument ||
                !canMergeDocuments ||
                !!contextMenuDocument.readOnly
              }
            >
              <ListItemIcon>
                <MergeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Merge..." />
            </MenuItem>
          </span>
        </Tooltip>
        {documentMergeCompareEnabled ? (
          <Tooltip
            title={getMenuTooltipTextForState(
              'document-merge-compare-menu',
              !!(
                contextMenuDocument &&
                canUseAsCompareAnchor(contextMenuDocument) &&
                canCompareDocuments
              ),
            )}
            placement="right"
          >
            <span>
              <MenuItem
                onClick={handleContextMenuCompare}
                disabled={!contextMenuDocument || !canCompareDocuments}
              >
                <ListItemIcon>
                  <CompareArrowsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Compare…" />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {documentSharingEnabled ? (
        <Tooltip title="Share a copy or grant live view-only access" placement="right">
          <span>
            <MenuItem onClick={handleContextMenuShare} disabled={!contextMenuDocument}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Share with user..." />
            </MenuItem>
          </span>
        </Tooltip>
        ) : null}
        {isEnabled(FEATURE_FLAG_KEYS.EXPORT_TO_FILE) ? (
            <MenuItem onClick={handleContextMenuExport}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Export .nbr archive..." />
            </MenuItem>
        ) : null}
        <Divider />
        <Tooltip title={getMenuTooltipTextForState('delete-document', !!contextMenuDocument)} placement="right">
          <span>
            <MenuItem onClick={handleContextMenuDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Delete" />
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <Menu
        open={grantContextMenu !== null}
        onClose={handleGrantContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          grantContextMenu !== null
            ? { top: grantContextMenu.mouseY, left: grantContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => void handleGrantContextMenuDuplicate()}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Duplicate to my library…"
            secondary={grantContextMenu?.grant.documentName}
          />
        </MenuItem>
        {documentMergeCompareEnabled ? (
          <MenuItem
            onClick={handleGrantContextMenuCompare}
            disabled={!canCompareDocuments}
          >
            <ListItemIcon>
              <CompareArrowsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Compare…"
              secondary={grantContextMenu?.grant.documentName}
            />
          </MenuItem>
        ) : null}
      </Menu>
    </StyledDrawer>
  );
};

export default DocumentPanel; 